import {
  createPropertyGroup,
  getPropertyGroupByID,
  patchPropertyGroup,
  putPropertyGroupContacts,
  putPropertyGroupProperties,
  putPropertyGroupRules,
  putPropertyGroupRulesets,
  putPropertyGroupUsers,
} from "api";
import { ACTION_TYPE, AppContext } from "context/appContext";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ActionFunction,
  Outlet,
  useLoaderData,
  useLocation,  
  useParams,
} from "react-router";
import { ROUTES, routeWithParams } from "router";
import {
  FormMode,
  PanelRootContext,
  PropertyGroup,
  TraxeroActionResponse,
} from "types";
import AuthService from "utils/AuthService";
import { getBlankPropertyGroup } from "utils";
import { VerticalTabLayout } from "components/layout/VerticalTabLayout";

export type PropertyGroupTabs =
  | "people"
  | "jobs"
  | "permits"
  | "properties"
  | "rule-config"
  | "permitTypes"
  | "group";

export interface PropertyGroupLoaderData {
  _propertyGroup: Promise<PropertyGroup>;
}

export interface PropertyGroupRouteParams {
  params: {
    tenantID?: string;
    groupID?: string;
  };
}

export enum PropertyGroupsActionHandler {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  UPDATE_PROPERTIES = "updateProperties",
  UPDATE_CONTACTS = "updateContacts",
  UPDATE_USERS = "updateUsers",
  UPDATE_RULES = "updateRules",
  UPDATE_RULESETS = "updateRulesets",
  UPDATE_QR_CODES = "updateQRCodes",
  UNKNOWN = "unknown",
}

export const propertyGroupLoader = async ({
  params,
}: PropertyGroupRouteParams): Promise<PropertyGroupLoaderData> => {
  const { tenantID, groupID } = params;
  return {
    _propertyGroup: getPropertyGroupByID({ tenantID, groupID }),
  };
};

export const PropertyGroupLayout = (): ReactNode => {
  const { state, dispatch } = useContext(AppContext);
  const { groupID } = useParams() as PropertyGroupRouteParams["params"];
  const { _propertyGroup } = useLoaderData() as PropertyGroupLoaderData;
  const location = useLocation();
  const [formMode, setFormMode] = useState<FormMode>("none");
  const [isLoading, setIsLoading] = useState(true);
  const [propertyGroup, setPropertyGroup] = useState<PropertyGroup>(
    getBlankPropertyGroup(state.tenant.id),
  );

  // permissions
  const allowedTabs: PropertyGroupTabs[] = useMemo(() => {
    const propertyGroupTabs: PropertyGroupTabs[] = [
      "group",
      "properties",
      "permitTypes",
      "jobs", // check view jobs
      "rule-config", // check view rules
      "people",
    ];
    if (AuthService.IsSuperAdmin()) {
      return propertyGroupTabs;
    }
    return propertyGroupTabs.filter((tab) => {
      switch (tab) {
        case "people":
        case "group":
          return true;
        case "jobs":
          return state.entityPermissions.job.read;
        case "rule-config":
          return state.entityPermissions.rule.read;
        case "properties":
          return state.entityPermissions.property.read;
        case "permitTypes":
          return (
            state.entityPermissions.permit.read && state.features.permitService
          );
        default:
          return false;
      }
    });
  }, []);

  // async loading
  useEffect(() => {
    const awaitLoader = async () => {
      const pg = await _propertyGroup;
      setPropertyGroup(pg);

      dispatch({
        type: ACTION_TYPE.SET_HEADER_TEXT,
        payload: `${pg.name}`,
      });
      dispatch({
        type: ACTION_TYPE.SET_ENTITY_NAME,
        payload: `${pg.name}`,
      });

      setIsLoading(false);
    };
    awaitLoader();
  }, [_propertyGroup]);

  // tab state
  const [tab, setTab] = useState<PropertyGroupTabs>("group");

  const groupDetailSubPaths = ['files', 'branding', 'qrCodes', 'donottow'];
  useEffect(() => {
    const splitPath = location.pathname.split("/");
    let currentTab;
    if (splitPath.length === 5 || groupDetailSubPaths.includes(splitPath[5])) {
      currentTab = "group";
    } else {
      currentTab = splitPath[5];
    }

    if (tab !== currentTab) {
      setTab(currentTab as PropertyGroupTabs);
    }
  }, [location.pathname]);

  // Effect to handle form mode from navigation state
  useEffect(() => {
    if (location.state?.formMode === "edit") {
      setFormMode("edit");
    } else {
      setFormMode("none");
    }
  }, [location.state]);

  const getTabRoute = (tab: PropertyGroupTabs): string => {
    let route: ROUTES;
    switch (tab) {
      case "group":
        route = ROUTES.PROPERTY_GROUP;
        break;
      case "properties":
        route = ROUTES.PROPERTY_GROUP_PROPERTIES;
        break;
      case "people":
        route = ROUTES.PROPERTY_GROUP_PEOPLE;
        break;
      case "jobs":
        route = ROUTES.PROPERTY_GROUP_JOBS;
        break;
      case "permitTypes":
        route = ROUTES.PROPERTY_GROUP_PERMIT_TYPES;
        break;
      case "rule-config":
        route = ROUTES.PROPERTY_GROUP_RULES;
        break;
      default:
        route = ROUTES.PROPERTY_GROUP;
        break;
    }
    return routeWithParams(route, [state.tenant.id, Number(groupID)]);
  };

  const onNavigate = useCallback(() => {
    setFormMode('none');
  }, []);

  return (
    <VerticalTabLayout
      title={propertyGroup.name}
      currentTab={tab}
      tabs={allowedTabs}
      getTabRoute={getTabRoute}
      isLoading={isLoading}
      onNavigate={onNavigate}
      >
      <Outlet
        context={
          {
            entity: propertyGroup,
            setEntity: setPropertyGroup,
            formMode,
            setFormMode,
            displayMode: "group",
            properties: propertyGroup.properties,
          } satisfies PanelRootContext<PropertyGroup>
        }
      />
    </VerticalTabLayout>
  );
};

export const propertyGroupsAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<PropertyGroupsActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, groupID } = params as PropertyGroupRouteParams["params"];
  let res: Response;

  try {
    switch (handler) {
      case PropertyGroupsActionHandler.CREATE:
        res = await createPropertyGroup({ body, tenantID });
        break;
      case PropertyGroupsActionHandler.DELETE:
      case PropertyGroupsActionHandler.UPDATE:
        res = await patchPropertyGroup({ groupID, tenantID, body });
        break;
      case PropertyGroupsActionHandler.UPDATE_CONTACTS:
        res = await putPropertyGroupContacts({ tenantID, groupID, body });
        break;
      case PropertyGroupsActionHandler.UPDATE_USERS:
        res = await putPropertyGroupUsers({ tenantID, groupID, body });
        break;
      case PropertyGroupsActionHandler.UPDATE_RULES:
        res = await putPropertyGroupRules({ tenantID, groupID, body });
        break;
      case PropertyGroupsActionHandler.UPDATE_RULESETS:
        res = await putPropertyGroupRulesets({ tenantID, groupID, body });
        break;
      case PropertyGroupsActionHandler.UPDATE_PROPERTIES:
        res = await putPropertyGroupProperties({ tenantID, groupID, body });
        break;
      default:
        res = new Response("Unknown handler", {
          status: 404,
          statusText: "Unknown Handler",
        });
    }
  } catch (error) {
    res = new Response("", { status: 500, statusText: "Network Error" });
  }
  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};
